import { useEffect, useState, useRef, useMemo } from "react";
import api from "@/axios";
import { getApiErrorWithStyle } from "@/utils/apiErrorHandler";

// Cache to store fetched data: key = `${endpoint}:${memberId}`, value = data
const dataCache = new Map<string, unknown>();

// Cache to store error states: key = `${endpoint}:${memberId}`, value = { error: string, isRed: boolean }
const errorCache = new Map<string, { error: string; isRed: boolean }>();

// In-flight requests: key = `${endpoint}:${memberId}`, value = Promise
const inflightRequests = new Map<string, Promise<unknown>>();

interface UseFetchCardOptions<T> {
  endpoint: string;
  memberId?: string;
  enabled?: boolean;
  cacheTime?: number;
  processData?: (data: unknown) => T | null;
  method?: "GET" | "POST";
  requestBody?: Record<string, unknown>;
}

interface UseFetchCardReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  isErrorRed: boolean;
  refetch: () => void;
}

/**
 * Determines error type and appropriate message/color based on HTTP status and error content
 */
const getErrorInfo = (err: any): { message: string; isRed: boolean } => {
  const status = err.response?.status;
  const errorMessage = err.response?.data?.message || err.message || "";

  // 1. ACCESS DENIED (403/401) - RED
  if (status === 403 || status === 401) {
    return {
      message: "You do not have access to this member's data.",
      isRed: true,
    };
  }

  // Check for access-related error messages
  const lowerMessage = errorMessage.toLowerCase();
  if (
    lowerMessage.includes("not accessible") ||
    lowerMessage.includes("access denied") ||
    lowerMessage.includes("unauthorized") ||
    lowerMessage.includes("permission denied")
  ) {
    return {
      message: "You do not have access to this member's data.",
      isRed: true,
    };
  }

  // 2. NO DATA FOUND (404 or NO_RECORDS) - GREY
  if (status === 404 || err.message === "NO_RECORDS") {
    return {
      message: "No records were found for this patient.",
      isRed: false,
    };
  }

  // Check for "no data" related messages
  if (
    lowerMessage.includes("no records") ||
    lowerMessage.includes("not found") ||
    lowerMessage.includes("no data") ||
    lowerMessage.includes("no table")
  ) {
    return {
      message: "No records were found for this patient.",
      isRed: false,
    };
  }

  // 3. NETWORK/SERVER ERROR (500, network issues, etc.) - RED
  if (status >= 500) {
    return {
      message: "Unable to connect to the server. Please try again later.",
      isRed: true,
    };
  }

  // Check for network-related errors
  if (
    err.code === "ERR_NETWORK" ||
    err.code === "ECONNABORTED" ||
    lowerMessage.includes("network") ||
    lowerMessage.includes("timeout") ||
    lowerMessage.includes("connection")
  ) {
    return {
      message: "Network error. Please check your connection and try again.",
      isRed: true,
    };
  }

  // 4. FALLBACK - Use centralized error handler
  const errorInfo = getApiErrorWithStyle(err);
  return {
    message: errorInfo.message,
    isRed: errorInfo.isRed,
  };
};

export function useFetchCard<T = unknown>({
  endpoint,
  memberId,
  enabled = true,
  cacheTime = 5 * 60 * 1000,
  processData,
  method = "GET",
  requestBody,
}: UseFetchCardOptions<T>): UseFetchCardReturn<T> {
  // Memoize serialized requestBody to prevent unnecessary effect re-runs
  // Only recalculate when requestBody content actually changes
  const serializedRequestBody = useMemo(() => {
    if (!requestBody) return null;
    try {
      return JSON.stringify(requestBody);
    } catch {
      return null;
    }
  }, [requestBody]);

  // Helper to generate cache key that includes requestBody for POST requests
  const getCacheKey = useMemo(() => {
    // Include requestBody in cache key for POST requests to prevent cache collisions
    const bodyKey = method === "POST" && serializedRequestBody 
      ? `:${serializedRequestBody}` 
      : "";
    return `${endpoint}:${memberId || "no-member"}${bodyKey}`;
  }, [endpoint, memberId, method, serializedRequestBody]);

  // Initialize state from cache if available (only when memberId and endpoint are provided)
  const getInitialState = () => {
    if (!enabled) {
      return {
        data: null,
        loading: false,
        error: null,
        isErrorRed: false,
      };
    }
    const cacheKey = getCacheKey;
    const cachedError = errorCache.get(cacheKey);
    const cachedData = dataCache.get(cacheKey) as T | undefined;

    return {
      data: cachedData || null,
      loading: !cachedData && !cachedError,
      error: cachedError?.error || null,
      isErrorRed: cachedError?.isRed || false,
    };
  };

  const initialState = getInitialState();
  const [data, setData] = useState<T | null>(initialState.data);
  const [loading, setLoading] = useState(initialState.loading);
  const [error, setError] = useState<string | null>(initialState.error);
  const [isErrorRed, setIsErrorRed] = useState(initialState.isErrorRed);

  // Use ref to track the latest memberId to prevent race conditions
  const currentMemberIdRef = useRef(memberId);
  const processDataRef = useRef(processData);
  const requestBodyRef = useRef(requestBody);

  // Update refs when values change
  useEffect(() => {
    currentMemberIdRef.current = memberId;
    processDataRef.current = processData;
    requestBodyRef.current = requestBody;
  }, [memberId, processData, requestBody]);

  // Sync state from cache when memberId or endpoint changes
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const cacheKey = getCacheKey;
    const cachedError = errorCache.get(cacheKey);
    const cachedData = dataCache.get(cacheKey) as T | undefined;

    if (cachedError) {
      setError(cachedError.error);
      setIsErrorRed(cachedError.isRed);
      setData(null);
      setLoading(false);
    } else if (cachedData) {
      setData(cachedData);
      setError(null);
      setIsErrorRed(false);
      setLoading(false);
    }
  }, [memberId, endpoint, enabled]);

  // Manual refetch function
  const refetch = () => {
    if (!enabled) return;

    const cacheKey = getCacheKey;
    dataCache.delete(cacheKey);
    errorCache.delete(cacheKey);
    inflightRequests.delete(cacheKey);

    // Trigger re-fetch by updating state
    setLoading(true);
    setError(null);
    setIsErrorRed(false);
    setData(null);
  };

  // Track the cache key and abort controller for the current request
  const currentRequestCacheKeyRef = useRef<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const abortControllerCacheKeyRef = useRef<string>("");
  // Track the previous cache key to detect actual changes
  const previousCacheKeyRef = useRef<string>("");

  useEffect(() => { // NOSONAR
    // Don't fetch if disabled
    if (!enabled) {
      setLoading(false);
      setData(null);
      setError(null);
      setIsErrorRed(false);
      return;
    }

    const currentCacheKey = getCacheKey;
    let isSubscribed = true;
    let abortController: AbortController | null = null;
    
    // Store the cache key for this effect run
    const effectCacheKey = currentCacheKey;
    // Track if we actually created a request for this effect
    let requestCreated = false;
    
    // Check if cache key actually changed (not just a re-render)
    // Store this before we potentially update previousCacheKeyRef
    const cacheKeyChanged = previousCacheKeyRef.current !== effectCacheKey && previousCacheKeyRef.current !== "";

    const fetchData = async () => {
      // Check error cache first - don't retry failed requests
      if (errorCache.has(currentCacheKey)) {
        const cachedError = errorCache.get(currentCacheKey)!;
        if (isSubscribed && currentMemberIdRef.current === memberId) {
          setError(cachedError.error);
          setIsErrorRed(cachedError.isRed);
          setData(null);
          setLoading(false);
        }
        return;
      }

      // Reset state at the start of fetch (only if not from error cache)
      if (isSubscribed) {
        setLoading(true);
        // Don't clear error here - let it persist if it exists
      }

      try {
        // Check cache first
        if (dataCache.has(currentCacheKey)) {
          const cachedData = dataCache.get(currentCacheKey) as T;
          if (isSubscribed && currentMemberIdRef.current === memberId) {
            setData(cachedData);
            setError(null);
            setIsErrorRed(false);
            setLoading(false);
          }
          return;
        }

        // Check if there's already an in-flight request for this data
        if (inflightRequests.has(currentCacheKey)) {
          const existingRequest = inflightRequests.get(currentCacheKey) as Promise<T>;
          const result = await existingRequest;

          if (currentMemberIdRef.current === memberId && isSubscribed) {
            setData(result);
            setError(null);
            setIsErrorRed(false);
            setLoading(false);
          }
          return;
        }

        // Only create AbortController if we're actually making a new request
        abortController = new AbortController();
        requestCreated = true;
        // Store abort controller and its cache key in refs so cleanup can check if it's still valid
        abortControllerRef.current = abortController;
        abortControllerCacheKeyRef.current = effectCacheKey;
        // Only update currentRequestCacheKeyRef when we actually create a request
        // This prevents premature cleanup of requests that haven't started yet
        currentRequestCacheKeyRef.current = effectCacheKey;
        // Update previous cache key ref only when we create a request
        // This ensures cleanup can properly detect cache key changes
        previousCacheKeyRef.current = effectCacheKey;

        // Use ref to get the latest requestBody to avoid stale closures
        const currentRequestBody = requestBodyRef.current;

        // Create new request
        const requestPromise = (
          method === "POST"
            ? api.post(memberId ? `${endpoint}/${memberId}` : endpoint, currentRequestBody || {}, {
                signal: abortController.signal,
              })
            : api.get(memberId ? `${endpoint}/${memberId}` : endpoint, {
                signal: abortController.signal,
              })
        )
          .then((res) => {
            const rawData = res.data;

            // Check if response has data
            if (!rawData || !rawData.data) {
              throw new Error("NO_RECORDS");
            }

            // Check for empty arrays
            if (Array.isArray(rawData.data) && rawData.data.length === 0) {
              throw new Error("NO_RECORDS");
            }

            // Check for empty objects
            if (
              !Array.isArray(rawData.data) &&
              typeof rawData.data === "object" &&
              Object.keys(rawData.data).length === 0
            ) {
              throw new Error("NO_RECORDS");
            }

            // Process data if processor provided
            const processedData = processDataRef.current
              ? processDataRef.current(rawData)
              : (rawData.data as T);

            // Check if processed data is empty (after processing)
            if (processedData === null || processedData === undefined) {
              throw new Error("NO_RECORDS");
            }

            // Check if processed data is an empty array
            if (Array.isArray(processedData) && processedData.length === 0) {
              throw new Error("NO_RECORDS");
            }

            // Check if processed data is an empty object
            if (
              !Array.isArray(processedData) &&
              typeof processedData === "object" &&
              Object.keys(processedData).length === 0
            ) {
              throw new Error("NO_RECORDS");
            }

            // Store in cache with expiry
            dataCache.set(currentCacheKey, processedData);
            // Clear any error cache since we have successful data
            errorCache.delete(currentCacheKey);
            setTimeout(() => {
              dataCache.delete(currentCacheKey);
            }, cacheTime);

            return processedData;
          })
          .finally(() => {
            inflightRequests.delete(currentCacheKey);
            // Clear abort controller ref when request completes
            if (abortControllerRef.current === abortController) {
              abortControllerRef.current = null;
              abortControllerCacheKeyRef.current = "";
            }
          });

        // Store the in-flight request
        inflightRequests.set(currentCacheKey, requestPromise);

        // Await the result
        const result = await requestPromise;

        // Only update state if this memberId is still current and component is subscribed
        if (currentMemberIdRef.current === memberId && isSubscribed) {
          setData(result);
          setError(null);
          setIsErrorRed(false);
        }
      } catch (err: any) {
        // Don't update state if request was aborted
        // Also check for axios cancellation (code === 'ERR_CANCELED')
        if (
          err.name === "CanceledError" || 
          err.name === "AbortError" ||
          err.code === "ERR_CANCELED" ||
          (err.message && err.message.includes("canceled"))
        ) {
          // Clear abort controller refs if this was the current request
          if (abortControllerRef.current === abortController) {
            abortControllerRef.current = null;
            abortControllerCacheKeyRef.current = "";
          }
          return;
        }

        // Only update error state if this memberId is still current and component is subscribed
        if (currentMemberIdRef.current !== memberId || !isSubscribed) {
          return;
        }

        console.error(`Failed to fetch ${endpoint}:`, err);

        // Get error info (message + color)
        const errorInfo = getErrorInfo(err);

        // Cache error state to prevent retries (especially for 404s and other client errors)
        const status = err.response?.status;
        // Cache 4xx errors (404, 403, etc.) - these won't change on retry
        // Don't cache 5xx errors or network errors - these might be temporary
        if (status && status >= 400 && status < 500) {
          errorCache.set(currentCacheKey, {
            error: errorInfo.message,
            isRed: errorInfo.isRed,
          });
          // Clear error cache after cacheTime (same as data cache)
          setTimeout(() => {
            errorCache.delete(currentCacheKey);
          }, cacheTime);
        }

        setError(errorInfo.message);
        setIsErrorRed(errorInfo.isRed);
        setData(null); // Clear any existing data on error
      } finally {
        // Only update loading if this memberId is still current and component is subscribed
        if (currentMemberIdRef.current === memberId && isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isSubscribed = false;
      // Only abort if:
      // 1. We actually created a request for this effect (requestCreated is true)
      // 2. The abort controller exists
      // 3. The cache key for this abort controller matches this effect's cache key
      //    (meaning this is the request we started, not a new one)
      // 4. The cache key has actually changed (not just a re-render with same dependencies)
      //    OR the abort controller is no longer the current one (new request started)
      if (
        requestCreated &&
        abortController && 
        abortControllerCacheKeyRef.current === effectCacheKey &&
        (
          // Cache key changed (memberId/endpoint/requestBody actually changed)
          cacheKeyChanged ||
          // Or abort controller was replaced (new request started for same cache key)
          abortControllerRef.current !== abortController
        )
      ) {
        abortController.abort();
        // Clear refs if this was the current controller
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
          abortControllerCacheKeyRef.current = "";
        }
      }
    };
  }, [
    endpoint,
    memberId,
    enabled,
    cacheTime,
    method,
    serializedRequestBody, // Use memoized serialized value instead of calling JSON.stringify on every render
  ]);

  return { data, loading, error, isErrorRed, refetch };
}
